/**
 * Wiki markdown export.
 *
 * Fetches the export endpoint and hands the reader the resulting file, with the
 * filename the server asks for (falling back to a sanitized owner-repo-wiki
 * name). Throws an Error whose message is safe to show the user.
 */

import { buildExportFilename } from '@/lib/wiki/export-filename';

export { buildExportFilename };

type ExportWikiMarkdownArgs = {
  owner: string;
  repo: string;
  version?: number | null;
  language?: string | null;
};

/**
 * Extracts filename from Content-Disposition header.
 * Handles both quoted and unquoted filename values.
 */
export function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) {
    return fallback;
  }
  // Match filename="value" or filename=value (RFC 6266)
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

export async function exportWikiMarkdown({
  owner,
  repo,
  version,
  language,
}: ExportWikiMarkdownArgs): Promise<void> {
  const params = new URLSearchParams({ owner, repo });
  if (version !== null && version !== undefined) {
    params.set('version', version.toString());
  }
  if (language) {
    params.set('language', language);
  }

  const response = await fetch(`/api/wiki/export?${params.toString()}`);

  if (!response.ok) {
    let errorDetails = 'Please try again in a moment.';

    try {
      const body = await response.json();
      if (typeof body?.error === 'string') {
        errorDetails = body.error.replace(/_/g, ' ');
      }
    } catch {
      // Ignore JSON parsing issues and keep the generic fallback message.
    }

    throw new Error(errorDetails);
  }

  const fallbackFilename = buildExportFilename(owner, repo, version);
  saveBlobAs(
    await response.blob(),
    extractFilename(response.headers.get('Content-Disposition'), fallbackFilename),
  );
}

/** Hands the reader `blob` as a downloaded file named `filename`. */
export function saveBlobAs(blob: Blob, filename: string): void {
  // Use a temporary anchor to trigger a download with the right filename.
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke after the click has been processed: Safari and older Firefox can
  // cancel the download if the URL is revoked synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
